import { memo, useState } from 'react';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Box from '@mui/material/Box';
import ArrowOutwardIcon from '@mui/icons-material/ArrowOutward';
//Local files
import BaseDrawer from 'components/Common/BaseDrawer/BaseDrawer';
import UrgentlyAdditionalCostForm from 'components/Orders/UrgentlyAdditionalCosts/Form/Form';
import UrgentlyAdditionalCostsTable from 'components/Orders/UrgentlyAdditionalCosts/Table/Table';
import useApp from 'hooks/useApp';
import useCustomSelector from 'hooks/useCustomSelector';
import useUrgentlyAdditionalCosts from 'hooks/useUrgentlyAdditionalCosts';
import useError from 'hooks/useError';
import { reasons, orderStatuses } from 'helpers/constants';

const UrgentlyAdditionalCostsDrawer = ({
  id,
  number,
  status
}) => {
  const { closeDrawer } = useApp();
  const { setError } = useError();
  const { getUrgentlyAdditionalCosts, clearLocalUrgentlyAdditionalCosts } = useUrgentlyAdditionalCosts();

  const { open, costs, isShowMore, nextOffset } = useCustomSelector(state => ({
    open: state.app.urgentlyAdditionalCosts?.open || false,
    costs: state.urgentlyAdditionalCosts.all.data,
    isShowMore: state.urgentlyAdditionalCosts.all.pagination.count + 
                 state.urgentlyAdditionalCosts.all.pagination.offset < 
                 state.urgentlyAdditionalCosts.all.pagination.total_count,
    nextOffset: state.urgentlyAdditionalCosts.all.pagination.limit + 
                state.urgentlyAdditionalCosts.all.pagination.offset
  }));

  const [processing, setProcessing] = useState(false);
  const [fetching, setFetching] = useState(false);
  const isRestrictedStatus = status === orderStatuses.NEW || status === orderStatuses.MANAGER_ACCEPTED;
  const disabled = processing || fetching || isRestrictedStatus;

  const fetchCosts = (offset) => {
    setFetching(true);
    getUrgentlyAdditionalCosts({ order_id: id, offset })
      .catch((error) => setError(error))
      .finally(() => setFetching(false));
  };

  const handleEntering = () => fetchCosts(0);
  const handleExiting = () => clearLocalUrgentlyAdditionalCosts();
  const onNextOffset = () => fetchCosts(nextOffset);

  return (
    <BaseDrawer
      grey
      open={open}
      disabled={!open}
      onClose={() => closeDrawer(reasons.URGENTLY_ADDITIONAL_COSTS)}
      paperSxProps={{ width: { xs: '100%', lg: '800px' } }}
      SlideProps={{ onEntering: handleEntering, onExited: handleExiting }}
      title='Urgently Additional Costs'
      content={
        <>
          <Card sx={{ p: '24px' }}>
            <Typography variant='title'>{`Order ${number}`}</Typography>
            <UrgentlyAdditionalCostForm 
              orderId={id}
              disabled={disabled} 
              processing={processing}
              onProcessing={setProcessing} 
            />
          </Card>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', pt: '40px' }}>
            <Button
              color='primary'
              variant='contained'
              endIcon={<ArrowOutwardIcon />}
              disabled={disabled}
              onClick={() => setProcessing(true)}
              sx={{ mb: 3 }}
            >
              Create Request
            </Button>
            
            {!!costs.length && (
              <UrgentlyAdditionalCostsTable 
                orderId={id}
                costs={costs}
                disabled={disabled}
                isShowMore={isShowMore}
                onNextOffset={onNextOffset}
              />
            )}
          </Box>
        </>
      }
    />
  );
};

export default memo(UrgentlyAdditionalCostsDrawer);
